import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AppState } from './core/app.state';
import { Store } from '@ngrx/store';
import { LanguageService } from './config/language.service';
import Hotjar from '@hotjar/browser'
import { environment } from 'src/environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { RuntimeConfigService } from './config/runtime-config.service';
import { SettingsReturnUrlService } from './core/services/settings-return-url.service';
import { delay, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading).pipe(delay(0));
  private _isScreenSizeValid: boolean = window.innerWidth >= 900;
  private maintenancePollId: ReturnType<typeof setInterval> | null = null;
  private currentUrl = '/';
  private routerSub!: Subscription;

  constructor(
    private languageService: LanguageService,
    private store: Store<AppState>,
    private router: Router,
    private runtimeConfigService: RuntimeConfigService,
    // Injected only so it starts tracking navigation from app start; the settings
    // sidebar reads it later to decide where "back" goes.
    private settingsReturnUrlService: SettingsReturnUrlService,
  ) {
    this.initHotjar();
  }

  ngOnInit() {
    this.languageService.setInitialLanguage();
    this._isScreenSizeValid = window.innerWidth >= 900;

    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.currentUrl = e.urlAfterRedirects;
    });

    this.enforceMaintenanceRoute();
    this.maintenancePollId = setInterval(() => {
      void this.pollMaintenanceState();
    }, 10_000);
  }

  ngOnDestroy(): void {
    if (this.maintenancePollId) {
      clearInterval(this.maintenancePollId);
      this.maintenancePollId = null;
    }
    this.routerSub?.unsubscribe();
  }

  isPopup(): boolean {
    return this.currentUrl.includes('unipile-callback');
  }

  isScreenSizeValid(): boolean {
    return this.isPopup() || this._isScreenSizeValid;
  }

  private async pollMaintenanceState(): Promise<void> {
    await this.runtimeConfigService.load();
    this.enforceMaintenanceRoute();
  }

  private enforceMaintenanceRoute(): void {
    const isOn = this.runtimeConfigService.maintenanceEnabled;
    const onMaintenancePage = this.currentUrl.startsWith('/maintenance');
    if (isOn && !onMaintenancePage) {
      void this.router.navigateByUrl('/maintenance', { replaceUrl: true });
    } else if (!isOn && onMaintenancePage) {
      void this.router.navigateByUrl('/quotations', { replaceUrl: true });
    }
  }

  initHotjar() {
    try {
      if (environment.HOTJAR_ENABLED && environment.HOTJAR_SITE_ID) {
        console.info('[Hotjar initialized]');
        Hotjar.init(environment.HOTJAR_SITE_ID, environment.HOTJAR_VERSION || 6, {
          debug: environment.HOTJAR_DEBUG ? true : false
        });
      }
    } catch (error) {
      console.error('[Hotjar initialization failed]', error);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(_: Event) {
    this._isScreenSizeValid = window.innerWidth >= 900;
  }
}
